'use client';

/**
 * RFQChat - RFQ-level message view and WhatsApp compose
 *
 * Shows messages specific to an RFQ with vendor contacts pre-loaded.
 * Focused on vendor communication for this RFQ.
 *
 * @component_role RFQ-level Chat
 * @business_rule Shows messages specific to this RFQ - vendor quote discussions,
 *   clarification requests, negotiation threads. Send WhatsApp to RFQ vendors
 *   directly. Contact dropdown pre-filtered to vendors on this RFQ only.
 * @user_outcome Focused conversation view - see only messages relevant to this
 *   specific quote request. Follow up with vendors without losing context.
 * @integration_point klyde_edge /neo/graph/compare/rfq, /messaging/whatsapp/send-to-contact
 *
 * @layer presentation
 * @arch_component_type widget
 * @arch_domain copilot
 */

import { useState } from 'react';
import { MessageSquare, FileText, Factory } from 'lucide-react';
import { toast } from 'sonner';
import { useRFQComparison, useVendorContacts, useSendWhatsApp } from '@/lib/api/hooks';
import { useCapabilityStore } from '@/lib/stores/capability-store';
import type { ContactSummary, WhatsAppMediaAttachment } from '@/lib/api/types';

// Shared components
import { LoadingState, EmptyState } from './shared';
import { ContactList } from './contact-selector';
import { WhatsAppCompose } from './whatsapp-compose';

// ============================================================
// Sub-Components
// ============================================================

interface RFQHeaderProps {
  rfqName: string;
  vendorName?: string;
}

function RFQHeader({ rfqName, vendorName }: RFQHeaderProps) {
  return (
    <div className="p-3 border-b border-panel-border shrink-0">
      <h4 className="text-xs font-semibold text-muted-foreground mb-2">RFQ MESSAGES</h4>
      <div className="p-2 rounded-lg bg-card border border-border">
        <div className="flex items-center gap-2">
          <FileText className="h-4 w-4 text-muted-foreground" />
          <span className="text-sm font-medium truncate">{rfqName}</span>
        </div>
        {vendorName && (
          <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground">
            <Factory className="h-3 w-3" />
            <span>{vendorName}</span>
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================================
// Main Component
// ============================================================

interface RFQChatProps {
  rfqId: string;
  projectId?: string | null;
}

export function RFQChat({ rfqId, projectId }: RFQChatProps) {
  const [selectedContact, setSelectedContact] = useState<ContactSummary | null>(null);
  const [whatsappMessage, setWhatsappMessage] = useState('');
  const [attachments, setAttachments] = useState<WhatsAppMediaAttachment[]>([]);

  const sendWhatsAppStrategy = useCapabilityStore((s) => s.strategyName('messaging', 'send_whatsapp'));
  const rfqComparisonStrategy = useCapabilityStore((s) => s.strategyName('rfi', 'comparison'));
  const vendorContactsStrategy = useCapabilityStore((s) => s.strategyName('graph_explore', 'vendor_contacts'));
  const sendWhatsApp = useSendWhatsApp(sendWhatsAppStrategy!);

  // Fetch RFQ data to get vendor info
  const { data: rfqData, isLoading: rfqLoading } = useRFQComparison(rfqComparisonStrategy!, { rfq_id: rfqId });

  // Get primary vendor from RFQ
  const primaryVendor = rfqData?.vendors?.[0];
  // Extract Odoo ID from semantic_id (format: "rfq:12345")
  const rfqOdooIdStr = rfqData?.rfq_semantic_id?.split(':')[1];
  const rfqOdooId = rfqOdooIdStr ? parseInt(rfqOdooIdStr, 10) : undefined;

  // Fetch vendor contacts
  const { data: contactsData, isLoading: contactsLoading } = useVendorContacts(
    vendorContactsStrategy!,
    primaryVendor ? { vendor_id: primaryVendor.semantic_id } : null
  );

  const contacts = contactsData?.contacts || [];

  // Loading state
  if (rfqLoading) {
    return <LoadingState message="Loading RFQ..." />;
  }

  // Handlers
  const handleSendWhatsApp = async () => {
    if (!rfqOdooId) {
      toast.error('Cannot send message', { description: 'RFQ details not loaded' });
      return;
    }

    if (!selectedContact?.phone) {
      toast.error('No phone number', { description: 'Please select a contact with a phone number' });
      return;
    }

    sendWhatsApp.mutate(
      {
        to: selectedContact.phone,
        message: whatsappMessage,
        context_model: 'purchase.order',
        context_id: rfqOdooId,
        media: attachments.length > 0 ? attachments : undefined,
      },
      {
        onSuccess: (data) => {
          if (data.success) {
            toast.success('WhatsApp sent', { description: `Sent to ${selectedContact.name}` });
            setWhatsappMessage('');
            setAttachments([]);
          } else {
            toast.error('Failed to send', { description: data.whatsapp_error || 'Unknown error' });
          }
        },
        onError: (err) => {
          toast.error('Send failed', { description: (err as Error).message });
        },
      }
    );
  };

  const canSend =
    !!selectedContact?.phone &&
    (!!whatsappMessage.trim() || attachments.length > 0) &&
    !sendWhatsApp.isPending;
  const recipientLabel = selectedContact ? `Send to ${selectedContact.name}` : 'Select a contact above';

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <RFQHeader
        rfqName={rfqData?.rfq_name || rfqId}
        vendorName={primaryVendor?.name}
      />

      {/* Vendor Contacts */}
      <div className="p-3 border-b border-panel-border">
        <h4 className="text-xs font-semibold text-muted-foreground mb-2">VENDOR CONTACTS</h4>
        <ContactList
          contacts={contacts}
          selectedContact={selectedContact}
          onSelect={setSelectedContact}
          isLoading={contactsLoading}
          maxDisplay={5}
        />
      </div>

      {/* Message area - placeholder for actual messages */}
      <div className="flex-1 overflow-y-auto p-3">
        <EmptyState
          icon={MessageSquare}
          title="RFQ message history coming soon"
          subtitle="Select a contact below to send a WhatsApp"
        />
      </div>

      {/* WhatsApp Compose */}
      <WhatsAppCompose
        message={whatsappMessage}
        onMessageChange={setWhatsappMessage}
        onSend={handleSendWhatsApp}
        canSend={canSend}
        isSending={sendWhatsApp.isPending}
        recipientLabel={recipientLabel}
        placeholder={selectedContact ? 'Type a message...' : 'Select a contact first...'}
        attachments={attachments}
        onAttachmentsChange={setAttachments}
      />
    </div>
  );
}
